/**
 * Utility to override browser's native alert() and confirm() functions
 * with the custom alert system.
 * 
 * This should be called once when the app initializes, after the AlertProvider
 * is mounted and the alert context is available.
 * 
 * Note: window.confirm() is synchronous and blocking, but our system is async.
 * The override will show the modal, but the return value will default to false
 * for compatibility. It's recommended to use the useAlert hook directly for
 * better async handling.
 */

let alertContext: {
  showAlert: (message: string, type?: 'info' | 'warning' | 'error' | 'success', title?: string) => Promise<void>;
  showConfirm: (
    message: string,
    onConfirm?: () => void | Promise<void>,
    onCancel?: () => void,
    type?: 'info' | 'warning' | 'error' | 'success',
    title?: string,
    confirmText?: string,
    cancelText?: string
  ) => Promise<boolean>;
} | null = null;

/**
 * Initialize the alert override system.
 * This should be called after the AlertProvider is mounted.
 */
export const initializeAlertOverride = (context: typeof alertContext) => {
  alertContext = context;

  // Override window.alert
  if (typeof window !== 'undefined') {
    const originalAlert = window.alert;
    window.alert = (message?: string) => {
      if (alertContext) {
        // Use info type by default for alerts
        alertContext.showAlert(String(message || ''), 'info', 'Alert');
        return;
      }
      // Fallback to native alert if context is not available
      originalAlert.call(window, message);
    };

    // Override window.confirm
    // Note: Native confirm is synchronous, but our system is async.
    // We'll show the modal, but return false immediately for compatibility.
    // The actual confirmation logic should use the async showConfirm directly.
    const originalConfirm = window.confirm;
    window.confirm = (message?: string): boolean => {
      if (alertContext) {
        // Show the confirm dialog (async)
        alertContext.showConfirm(
          String(message || ''),
          undefined,
          undefined,
          'warning',
          'Confirm'
        );
        // Return false by default since we can't block synchronously
        // Code using window.confirm should be migrated to use showConfirm directly
        return false;
      }
      // Fallback to native confirm if context is not available
      return originalConfirm.call(window, message);
    };
  }
};

/**
 * Restore original browser alert and confirm functions.
 * Useful for testing or if you need to revert the override.
 */
export const restoreOriginalAlerts = () => {
  // Note: We can't fully restore the original functions once overridden,
  // but we can clear the context reference
  alertContext = null;
};

